import { palette } from '../../../palette';
import {
  FundingStatus,
  NetSettlementStatus,
  AttorneysFeeStatus,
  DeductionStatus,
  SettlementStatus,
  StatusMap,
  PaymentMethodStatus,
  DispositionStatus,
  ApprovalStatus,
  SettlementDistributionStatus,
} from '../../../types';

export const FundingStatusMap: StatusMap<FundingStatus> = {
  NotStarted: { color: palette.warning.main, label: 'Not Started' },
  Initializing: { color: palette.warning.main, label: 'Initializing' },
  Processing: { color: palette.warning.main, label: 'Processing' },
  Error: { color: palette.error.main, label: 'Error' },
  CriticalError: { color: palette.error.main, label: 'Critical Error' },
  Completed: { color: palette.success.main, label: 'Completed' },
};

export const NetSettlementStatusMap: StatusMap<NetSettlementStatus> = {
  Funding: { color: palette.warning.main, label: 'Funding' },
  Paid: { color: palette.success.main, label: 'Paid' },
  Failed: { color: palette.error.main, label: 'Failed' },
  InDistribution: { color: '#4aaac0', label: 'In Distribution' },
  NotStarted: { color: palette.text.disabled, label: 'Not Started' },
};

export const AttorneysFeeStatusMap: StatusMap<AttorneysFeeStatus> = {
  Funding: { color: palette.warning.main, label: 'Funding' },
  Paid: { color: palette.success.main, label: 'Paid' },
  Failed: { color: palette.error.main, label: 'Failed' },
  InDistribution: { color: '#4aaac0', label: 'In Distribution' },
  NotStarted: { color: palette.text.disabled, label: 'Not Started' },
};

export const DeductionStatusMap: StatusMap<DeductionStatus> = {
  Funding: { color: palette.warning.main, label: 'Funding' },
  Paid: { color: palette.success.main, label: 'Paid' },
  Failed: { color: palette.error.main, label: 'Failed' },
  InDistribution: { color: '#4aaac0', label: 'In Distribution' },
  NotStarted: { color: palette.text.disabled, label: 'Not Started' },
  Withhold: { color: palette.common.white, label: 'Withhold' }
};

export const DeductionTypeMap: Record<string, string> = {
  SERVICE_PROVIDER: 'Service Provider',
  Advance: 'Expense Reimbursement',
  Lien: 'Lien',
};

export const SettlementStatusMap: StatusMap<SettlementStatus> = {
  Draft: { color: palette.warning.main, label: 'Draft' },
  Funding: { color: palette.warning.main, label: 'Funding' },
  Paid: { color: palette.success.main, label: 'Paid' },
  Failed: { color: palette.error.main, label: 'Failed' },
  InDistribution: { color: '#4aaac0', label: 'In Distribution' },
  NotStarted: { color: palette.text.disabled, label: 'Not Started' },
};

export const SettlementPaymentFilterStatusMap: Record<string, { iconName: string; color: string }> = {
  Funding: { iconName: 'Funding', color: '#FFA500' },
  Paid: { iconName: 'Paid', color: '#00C853' },
  Failed: { iconName: 'Failed', color: '#F44336' },
  NotStarted: { iconName: 'NotStarted', color: palette.text.disabled },
  InDistribution: { iconName: 'InDistribution', color: '#4aaac0' },
};

export const DetailsFilter = [
  { value: 'Funding', label: 'Funding' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Failed', label: 'Failed' },
  { value: 'NotStarted', label: 'Not Started' },
  { value: 'InDistribution', label: 'In Distribution' },
];

export const PaymentMethodStatusMap: StatusMap<PaymentMethodStatus> = {
  ACH: { color: palette.success.main, label: 'ACH' },
  CHECK: { color: palette.success.main, label: 'CHECK' },
  V: { color: palette.success.main, label: 'V' },
};

export const DispositionStatusMap: StatusMap<DispositionStatus> = {
  PAY: { color: palette.common.white, label: 'PAY' },
  EXTERNAL: { color: palette.common.white, label: 'EXTERNAL' },
  WITHHOLD: { color: palette.common.white, label: 'WITHHOLD' },
};

// Map display titles to API type values for deductions
export const DeductionTypeMapping: Record<string, string> = {
  'Lien': 'Lien',
  'Service Provider': 'SERVICE_PROVIDER',
  'Advance': 'Advance'
};

export const SETTLEMENT_TYPE_OPTIONS = {
  standard: {
    value: 'standard',
    label: 'Settlement',
    description: 'The case settlement. Follows the case\'s fee arrangement unless you set fees for this settlement.',
  },
  standardPip: {
    value: 'standard_pip',
    label: 'Settlement + PIP',
    description: 'One memo with a settlement amount and a PIP amount. Fees and deductions apply to the settlement portion only.',
  },
  standardLienReimbursement: {
    value: 'lien_repayment',
    label: 'Settlement + Lien Reimbursement',
    description: 'Redistributes lien funds that were returned to the trust',
  },
};

export const ApprovalStatusMap: StatusMap<ApprovalStatus> = {
  WAITING_FOR_SUBMISSION: { color: palette.secondary.main, label: 'Waiting For Submission' },
  PENDING_REVIEW: { color: palette.warning.main, label: 'Pending Review' },
  APPROVED: { color: palette.success.main, label: 'Approved' },
  CHANGES_REQUESTED: { color: palette.error.main, label: 'Changes Requested' },
  REQUESTED_CHANGES: { color: palette.error.main, label: 'Changes Requested' },
  OVERRIDDEN: { color: palette.secondary.main, label: 'Overridden' },
  NOT_AVAILABLE: { color: palette.text.disabled, label: 'Not Available' },
};

export const SettlementDistributionStatusMap: StatusMap<SettlementDistributionStatus> = {
  Paid: { color: palette.success.main, label: 'Paid' },
  Funding: { color: palette.warning.main, label: 'Funding' },
  NotStarted: { color: palette.text.disabled, label: 'Not Started' },
  Failed: { color: palette.error.main, label: 'Failed' },
  InDistribution: { color: '#4aaac0', label: 'In Distribution' },
};

export const LedgerStatusMap: Record<string, { color: string; label: string }> = {
  POSTED: { color: palette.success.main, label: 'Posted' },
  INTRANSIT: { color: palette.warning.main, label: 'In Transit' },
  COMMITTED: { color: palette.text.disabled, label: 'Committed' },
};

// Case ledger category labels; kept separate from SETTLEMENT_TYPE_FILTER_OPTIONS so the filter can change independently
export const LedgerCategoryMap: Record<string, string> = {
  STANDARD: 'Settlement',
  STANDARD_PIP: 'Settlement + PIP',
  LIEN_REPAYMENT: 'Settlement + Lien Reimbursement',
  LIEN_REIMBURSEMENT: 'Lien Recovery',
};